import { useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '@/lib/firebase';
import { userAtom, userSettingsAtom } from '@/stores/auth';
import { UserSettings } from '@/types';
import { DEFAULT_USER_SETTINGS } from '@/constants';

export function useSettings() {
  const [user, setUser] = useAtom(userAtom);
  const settings = useAtomValue(userSettingsAtom);

  const updateSettings = useCallback(
    async (updates: Partial<UserSettings>) => {
      if (!user) {
        // Auth state is invalid - App.tsx will handle the force logout
        console.warn('Cannot update settings: user not logged in');
        return;
      }

      // Always merge with defaults first, then existing settings, then updates
      // This ensures we have a complete settings object even for users
      // whose Firestore document doesn't have all settings fields
      const currentSettings = settings ?? {};
      const updatedSettings: UserSettings = {
        ...DEFAULT_USER_SETTINGS,
        ...currentSettings,
        ...updates,
      };
      setUser({
        ...user,
        settings: updatedSettings,
      });

      try {
        // Persist to Firestore
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          settings: updatedSettings,
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        console.error('Failed to update settings:', error);
        // Revert optimistic update on failure
        setUser(user);
        throw error;
      }
    },
    [user, settings, setUser]
  );

  const updateSingleSetting = useCallback(
    async <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
      return updateSettings({ [key]: value });
    },
    [updateSettings]
  );

  const resetToDefaults = useCallback(async () => {
    return updateSettings(DEFAULT_USER_SETTINGS);
  }, [updateSettings]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Failed to sign out:', error);
      throw error;
    }
  }, []);

  return {
    settings: settings ?? DEFAULT_USER_SETTINGS,
    user,
    updateSettings,
    updateSingleSetting,
    resetToDefaults,
    signOut: handleSignOut,
    isLoading: user === undefined,
  };
}
